import { parseSchema } from './parser.js';
import { diffSchemas } from './diff.js';
import { getSql, setSql } from './editor.js';

let lastDiff = [], lastAst = {};

export function run() {
  const localSql = getSql('local').trim();
  const prodSql = getSql('prod').trim();
  const dialect = document.getElementById('dialect')?.value || 'mysql';
  const options = {
    cmpComment: document.getElementById('cmpComment')?.checked ?? true,
    enableRisk: document.getElementById('enableRisk')?.checked ?? true,
    genRollback: document.getElementById('genRollback')?.checked ?? false,
    denyDrop: document.getElementById('denyDrop')?.checked ?? true
  };

  const a = parseSchema(localSql, dialect);
  const b = parseSchema(prodSql, dialect);
  lastAst = { local: a, prod: b };
  lastDiff = diffSchemas(a, b, options);
  render(dialect, options);
}

function render(dialect, options) {
  const rollbackBox = document.getElementById('rollbackBox');
  const diffBox = document.getElementById('diff');
  const allowAddCol = document.getElementById('allowAddCol')?.checked ?? true;
  const allowAddIdx = document.getElementById('allowAddIndex')?.checked ?? true;
  const searchTerm = document.getElementById('diffSearch')?.value.toLowerCase() || '';

  const upPhases = { create: [], alter: [], fk: [] };
  const downPhases = { fk: [], alter: [], drop: [] };
  const sortedTableNames = topologicalSort(Object.keys(lastAst.local), lastAst.local);

  lastDiff.forEach(d => {
    const { up, down } = generateSqlPair(d, dialect);
    const riskPrefix = options.enableRisk && d.risk !== 'GREEN' ? `-- [RISK: ${d.risk}] ` : '';

    // 安全逻辑：判断是否是“危险操作” (删除类或重命名类)
    const isDangerous = d.op.includes('DROP') || d.op.includes('RENAME');
    const isAddCol = d.op === 'ADD_COLUMN';
    const isAddIdx = d.op === 'ADD_INDEX';

    // 过滤正向 SQL
    let skipUp = false;
    if (isAddCol && !allowAddCol) skipUp = true;
    if (isAddIdx && !allowAddIdx) skipUp = true;
    if (isDangerous && options.denyDrop) skipUp = true;

    if (d.op === 'CREATE_TABLE') {
      upPhases.create.push({ table: d.table, sql: up });
      downPhases.drop.push(down);
    } else if (d.op === 'DROP_TABLE') {
      if (!skipUp) upPhases.alter.push(riskPrefix + up);
      if (down) upPhases.create.push({ table: d.table, sql: down });
    } else if (d.op.includes('FK')) {
      if (up && !skipUp) upPhases.fk.push(riskPrefix + up);
      if (down) downPhases.fk.push(down);
    } else {
      if (up && !skipUp) upPhases.alter.push(riskPrefix + up);
      if (down) downPhases.alter.push(down);
    }
  });

  setSql('output', finalJoin(upPhases, sortedTableNames));
  if (options.genRollback && (downPhases.fk.length > 0 || downPhases.alter.length > 0 || downPhases.drop.length > 0)) {
    rollbackBox.classList.remove('hidden');
    setSql('rollback', finalDown(downPhases));
  } else rollbackBox.classList.add('hidden');

  const filteredDiffs = lastDiff.filter(d => d.table.toLowerCase().includes(searchTerm));
  renderGroupedDiff(diffBox, filteredDiffs, options);

  if (document.getElementById('showAst')?.checked) {
    document.getElementById('astBox').classList.remove('hidden');
    document.getElementById('ast').textContent = JSON.stringify(lastAst, null, 2);
  } else document.getElementById('astBox').classList.add('hidden');
}

function finalJoin(phases, sortedNames) {
  const lines = [
    ...sortedNames.map(n => phases.create.find(p => p.table === n)?.sql).filter(Boolean),
    ...phases.alter,
    ...phases.fk
  ];
  return lines.join('\n\n') || '-- No changes detected';
}

function finalDown(phases) {
  return [...phases.fk, ...phases.alter, ...phases.drop].join('\n\n');
}

function topologicalSort(tables, ast) {
  const visited = new Set(), result = [], visiting = new Set();
  function visit(n) {
    if (visiting.has(n) || visited.has(n)) return;
    visiting.add(n);
    const deps = [];
    if (ast[n]?.foreignKeys) Object.values(ast[n].foreignKeys).forEach(fk => { if (tables.includes(fk.refTable)) deps.push(fk.refTable); });
    deps.forEach(visit);
    visiting.delete(n);
    visited.add(n);
    result.push(n);
  }
  tables.forEach(visit);
  return result;
}

function generateSqlPair(d, dialect) {
  let up = '', down = '';
  const isMy = dialect === 'mysql';
  const getColDef = (name, col) => `${name} ${col.type}${col.nullable ? '' : ' NOT NULL'}${col.defaultValue ? ` DEFAULT ${col.defaultValue}` : ''}${col.comment ? ` COMMENT '${col.comment}'` : ''}`;

  if (d.op === 'CREATE_TABLE') {
    const cols = Object.entries(d.new.columns).map(([n, def]) => "  " + getColDef(n, def));
    const idxs = Object.entries(d.new.indexes).map(([n, def]) => {
      const type = def.type === 'PRIMARY KEY' ? 'PRIMARY KEY' : (def.type === 'UNIQUE KEY' ? 'UNIQUE KEY ' + n : 'KEY ' + n);
      return `  ${type} (${def.columns.join(', ')})`;
    });
    up = `CREATE TABLE ${isMy ? '`'+d.table+'`' : '"'+d.table+'"'} (\n${[...cols, ...idxs].join(',\n')}\n)${isMy ? ` ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COMMENT='${d.new.comment || ''}'` : ''};`;
    down = `DROP TABLE ${isMy ? '`'+d.table+'`' : '"'+d.table+'"'};`;
    return { up, down };
  }

  if (d.op === 'DROP_TABLE') {
    const cols = Object.entries(d.old.columns).map(([n, def]) => "  " + getColDef(n, def));
    up = `DROP TABLE ${isMy ? '`'+d.table+'`' : '"'+d.table+'"'};`;
    down = `CREATE TABLE ${isMy ? '`'+d.table+'`' : '"'+d.table+'"'} (\n${cols.join(',\n')}\n)${isMy ? ` ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COMMENT='${d.old.comment || ''}'` : ''};`;
    return { up, down };
  }

  const q = isMy ? '`' : '"';
  if (isMy) {
    if (d.op === 'ADD_COLUMN') { up = `ALTER TABLE ${q}${d.table}${q} ADD COLUMN ${getColDef(d.column, d.new)};`; down = `ALTER TABLE ${q}${d.table}${q} DROP COLUMN ${q}${d.column}${q};`; }
    else if (d.op === 'MODIFY_COLUMN') { up = `ALTER TABLE ${q}${d.table}${q} MODIFY COLUMN ${getColDef(d.column, d.new)};`; down = `ALTER TABLE ${q}${d.table}${q} MODIFY COLUMN ${getColDef(d.column, d.old)};`; }
    else if (d.op === 'RENAME_COLUMN') { up = `ALTER TABLE ${q}${d.table}${q} CHANGE COLUMN ${q}${d.from}${q} ${getColDef(d.to, d.new)};`; down = `ALTER TABLE ${q}${d.table}${q} CHANGE COLUMN ${q}${d.to}${q} ${getColDef(d.from, d.new)};`; }
    else if (d.op === 'ADD_INDEX') { up = `ALTER TABLE ${q}${d.table}${q} ADD ${d.new.type} ${q}${d.index}${q} (${d.new.columns.join(', ')});`; down = `ALTER TABLE ${q}${d.table}${q} DROP INDEX ${q}${d.index}${q};`; }
    else if (d.op === 'MODIFY_INDEX') { up = `ALTER TABLE ${q}${d.table}${q} DROP INDEX ${q}${d.index}${q};\nALTER TABLE ${q}${d.table}${q} ADD ${d.new.type} ${q}${d.index}${q} (${d.new.columns.join(', ')});`; down = `ALTER TABLE ${q}${d.table}${q} DROP INDEX ${q}${d.index}${q};\nALTER TABLE ${q}${d.table}${q} ADD ${d.old.type} ${q}${d.index}${q} (${d.old.columns.join(', ')});`; }
    else if (d.op === 'MODIFY_TABLE_COMMENT') { up = `ALTER TABLE ${q}${d.table}${q} COMMENT = '${d.to}';`; down = `ALTER TABLE ${q}${d.table}${q} COMMENT = '${d.from}';`; }
    else if (d.op === 'ADD_FK') { up = `ALTER TABLE ${q}${d.table}${q} ADD CONSTRAINT ${q}${d.fk}${q} FOREIGN KEY (${q}${d.new.column}${q}) REFERENCES ${q}${d.new.refTable}${q} (${q}${d.new.refColumn}${q});`; down = `ALTER TABLE ${q}${d.table}${q} DROP FOREIGN KEY ${q}${d.fk}${q};`; }
    else if (d.op === 'DROP_COLUMN') { up = `ALTER TABLE ${q}${d.table}${q} DROP COLUMN ${q}${d.column}${q};`; down = `ALTER TABLE ${q}${d.table}${q} ADD COLUMN ${getColDef(d.column, d.old)};`; }
    else if (d.op === 'DROP_INDEX') { up = `ALTER TABLE ${q}${d.table}${q} DROP INDEX ${q}${d.index}${q};`; down = `ALTER TABLE ${q}${d.table}${q} ADD ${d.old.type} ${q}${d.index}${q} (${d.old.columns.join(', ')});`; }
    else if (d.op === 'DROP_FK') { up = `ALTER TABLE ${q}${d.table}${q} DROP FOREIGN KEY ${q}${d.fk}${q};`; down = `ALTER TABLE ${q}${d.table}${q} ADD CONSTRAINT ${q}${d.fk}${q} FOREIGN KEY (${q}${d.old.column}${q}) REFERENCES ${q}${d.old.refTable}${q} (${q}${d.old.refColumn}${q});`; }
  } else {
    if (d.op === 'ADD_COLUMN') { up = `ALTER TABLE "${d.table}" ADD COLUMN ${d.column} ${d.new.type};`; down = `ALTER TABLE "${d.table}" DROP COLUMN "${d.column}";`; }
    else if (d.op === 'RENAME_COLUMN') { up = `ALTER TABLE "${d.table}" RENAME COLUMN "${d.from}" TO "${d.to}";`; down = `ALTER TABLE "${d.table}" RENAME COLUMN "${d.to}" TO "${d.from}";`; }
    else if (d.op === 'ADD_FK') { up = `ALTER TABLE "${d.table}" ADD CONSTRAINT "${d.fk}" FOREIGN KEY ("${d.new.column}") REFERENCES "${d.new.refTable}" ("${d.new.refColumn}");`; down = `ALTER TABLE "${d.table}" DROP CONSTRAINT "${d.fk}";`; }
    else if (d.op === 'DROP_COLUMN') { up = `ALTER TABLE "${d.table}" DROP COLUMN "${d.column}";`; down = `ALTER TABLE "${d.table}" ADD COLUMN ${d.column} ${d.old.type};`; }
  }
  return { up, down };
}

function renderGroupedDiff(container, diffs, options) {
  const grouped = {};
  diffs.forEach(d => { if (!grouped[d.table]) grouped[d.table] = []; grouped[d.table].push(d); });
  const tableNames = Object.keys(grouped);
  if (tableNames.length === 0) { container.innerHTML = '<div class="text-center py-8 text-slate-400 italic font-medium">No match found.</div>'; return; }
  container.innerHTML = tableNames.map(tableName => {
    const tableDiffs = grouped[tableName];
    const riskCounts = { RED: 0, YELLOW: 0, GREEN: 0 };
    tableDiffs.forEach(d => riskCounts[d.risk]++);
    const badge = riskCounts.RED > 0 ? 'bg-red-500' : (riskCounts.YELLOW > 0 ? 'bg-yellow-500' : 'bg-green-500');
    return `<details class="group bg-white dark:bg-slate-900 border dark:border-slate-800 rounded-xl mb-3 shadow-sm overflow-hidden" open><summary class="flex justify-between items-center p-3 cursor-pointer bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"><div class="flex items-center gap-2"><span class="w-2 h-2 rounded-full ${badge}"></span><span class="font-bold text-sm text-slate-700 dark:text-slate-200 uppercase tracking-tight">${tableName}</span></div><svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-slate-400 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg></summary><div class="p-3 space-y-2 border-t border-slate-100 dark:border-slate-800">${tableDiffs.map(d => renderDiffItem(d, options)).join('')}</div></details>`;
  }).join('');
}

function renderDiffItem(d, options) {
  const riskColor = options.enableRisk ? (d.risk === 'RED' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : (d.risk === 'YELLOW' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400')) : 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400';
  let label = d.column || d.index || d.fk || 'TABLE';
  if (d.op === 'RENAME_COLUMN') label = `<del class="opacity-50">${d.from}</del> → <ins class="no-underline font-bold">${d.to}</ins>`;
  return `<div class="flex flex-col gap-1 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"><div class="flex justify-between items-center"><span class="font-mono text-[11px] font-bold">${label}</span><span class="px-2 py-0.5 text-[9px] font-black rounded uppercase ${riskColor}">${d.op}</span></div>${d.changes ? `<ul class="space-y-0.5">${d.changes.map(c => `<li class="text-[10px] text-slate-500">${c.field}: <del class="px-1 text-red-400">${c.from}</del> → <ins class="px-1 text-green-600 font-bold">${c.to}</ins></li>`).join('')}</ul>` : ''}</div>`;
}

export function getLastDiff() { return lastDiff; }
export function getLastAst() { return lastAst; }
